import { useMemo, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import CustomToast from 'components/common/CustomToast';
import usePinSpaceMessage from './usePinSpaceMessage';
import useUnpinSpaceMessage from './useUnpinSpaceMessage';
import { SpaceMessage } from 'types/spaceTypes';

interface UsePinnedMessagesManagerProps {
    spaceId: string;
    allMessages: SpaceMessage[];
    onMessagesRefetch?: () => void;
}

interface UsePinnedMessagesManagerReturn {
    pinnedMessages: SpaceMessage[];
    handlePinMessage: (messageId: string) => Promise<void>;
    handleUnpinMessage: (messageId: string) => Promise<void>;
    isPinning: boolean;
    isUnpinning: boolean;
    pinError: any;
    unpinError: any;
}

const usePinnedMessagesManager = ({
    spaceId,
    allMessages,
    onMessagesRefetch
}: UsePinnedMessagesManagerProps): UsePinnedMessagesManagerReturn => {
    
    // Pin/Unpin hooks
    const [pinSpaceMessage, pinSpaceMessageResult] = usePinSpaceMessage();
    const [unpinSpaceMessage, unpinSpaceMessageResult] = useUnpinSpaceMessage();

    // Memoized pinned messages
    const pinnedMessages = useMemo(() => {
        return allMessages.filter((message) => message.isPinned);
    }, [allMessages]);

    // Track previous success states to prevent infinite loops
    const prevPinSuccessRef = useRef(false);
    const prevUnpinSuccessRef = useRef(false);

    // Handle successful pin/unpin operations
    useEffect(() => {
        const pinJustSucceeded = pinSpaceMessageResult.isSuccess && !prevPinSuccessRef.current;
        const unpinJustSucceeded = unpinSpaceMessageResult.isSuccess && !prevUnpinSuccessRef.current;

        if (pinJustSucceeded || unpinJustSucceeded) {
            // Trigger messages refetch to get updated pin status
            if (onMessagesRefetch) {
                onMessagesRefetch();
            }
        }

        // Update the previous success state refs
        prevPinSuccessRef.current = pinSpaceMessageResult.isSuccess;
        prevUnpinSuccessRef.current = unpinSpaceMessageResult.isSuccess;
    }, [pinSpaceMessageResult.isSuccess, unpinSpaceMessageResult.isSuccess, onMessagesRefetch]);

    // Pin message handler
    const handlePinMessage = async (messageId: string): Promise<void> => {
        if (!spaceId) {
            toast.custom((t) => (
                <CustomToast t={t} message="Space ID is required" type="error" />
            ));
            return;
        }

        try {
            await pinSpaceMessage(spaceId, messageId);
            toast.custom((t) => (
                <CustomToast t={t} message="Message pinned successfully" type="success" />
            ));
        } catch (error) {
            console.error('Pin message failed:', error);
            toast.custom((t) => (
                <CustomToast t={t} message="Failed to pin message" type="error" />
            ));
        }
    };

    // Unpin message handler
    const handleUnpinMessage = async (messageId: string): Promise<void> => {
        if (!spaceId) {
            toast.custom((t) => (
                <CustomToast t={t} message="Space ID is required" type="error" />
            ));
            return;
        }

        try {
            await unpinSpaceMessage(spaceId, messageId);
            toast.custom((t) => (
                <CustomToast t={t} message="Message unpinned successfully" type="success" />
            ));
        } catch (error) {
            console.error('Unpin message failed:', error);
            toast.custom((t) => (
                <CustomToast t={t} message="Failed to unpin message" type="error" />
            ));
        }
    };

    return {
        pinnedMessages,
        handlePinMessage,
        handleUnpinMessage,
        isPinning: pinSpaceMessageResult.isLoading,
        isUnpinning: unpinSpaceMessageResult.isLoading,
        pinError: pinSpaceMessageResult.error,
        unpinError: unpinSpaceMessageResult.error,
    };
};

export default usePinnedMessagesManager;
